import { z } from 'zod/v4';

/**
 * The entity types onboarded to the custom attributes store.
 */
export type CustomAttributeEntityType = z.infer<typeof CustomAttributeEntityTypeRuntype>;
export const CustomAttributeEntityTypeRuntype = z.enum([
    'customer',
    'project',
    'component',
    'supplier',
    'bom_item',
    'off_the_shelf_part',
    'manufacturer',
]);

/**
 * Keys are 1-64 characters of lowercase letters, digits, `_`, `.` and `-`.
 */
export const CustomAttributeKeyRuntype = z
    .string()
    .describe('Custom attribute key: 1-64 characters of lowercase letters, digits, `_`, `.` and `-`.');

export type CustomAttributeDefinitionDTO = z.infer<typeof CustomAttributeDefinitionRuntype>;
export const CustomAttributeDefinitionRuntype = z.object({
    id: z.string(),
    entity_type: CustomAttributeEntityTypeRuntype,
    key: CustomAttributeKeyRuntype,
    description: z.string(),
    display_name: z.string().nullable(),
    source: z.string().nullable(),
    created_by: z.string().nullable(),
    created_at: z.string(),
    updated_by: z.string().nullable(),
    updated_at: z.string().nullable(),
});

export type RegisterCustomAttributeDefinitionDTO = z.infer<typeof RegisterCustomAttributeDefinitionRuntype>;
export const RegisterCustomAttributeDefinitionRuntype = z.object({
    entity_type: CustomAttributeEntityTypeRuntype,
    key: CustomAttributeKeyRuntype,
    description: z.string().describe('What the key holds. Required, at most 512 characters.'),
    display_name: z.string().optional().describe('Human-readable label for the key, at most 64 characters.'),
    source: z.string().optional().describe('Where the values come from, at most 64 characters.'),
});

export type EntityCustomAttributePatchDTO = z.infer<typeof EntityCustomAttributePatchRuntype>;
export const EntityCustomAttributePatchRuntype = z.object({
    entity_id: z.string(),
    set: z
        .record(CustomAttributeKeyRuntype, z.string())
        .optional()
        .describe('Keys to write, with their values. Values are opaque strings of at most 512 characters.'),
    remove: z.array(CustomAttributeKeyRuntype).optional().describe('Keys to drop. Must not overlap with `set`.'),
});

export type EntityCustomAttributesDTO = z.infer<typeof EntityCustomAttributesRuntype>;
export const EntityCustomAttributesRuntype = z.object({
    entity_id: z.string(),
    attributes: z.record(CustomAttributeKeyRuntype, z.string()),
});

export type BulkCustomAttributesDTO = z.infer<typeof BulkCustomAttributesRuntype>;
export const BulkCustomAttributesRuntype = z.object({
    items: z.array(EntityCustomAttributesRuntype),
});
